'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { Loader2, Users } from 'lucide-react';
import SongSearch from './SongSearch';
import type { Song } from '@/lib/songs';
import { ALL_SONGS } from '@/lib/songs';

type BandFormValues = {
  bandName: string;
  contactName: string;
  contactEmail: string;
  songsKnown: string;
  notes: string;
};

type Props = {
  onSuccess: (data: { bandName: string; song: Song }) => void;
};

export default function BandForm({ onSuccess }: Props) {
  const [selectedSong, setSelectedSong] = useState<Song | null>(null);
  const [songError, setSongError] = useState('');
  const [loading, setLoading] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<BandFormValues>({ mode: 'onBlur' });

  function onSubmit(data: BandFormValues) {
    setSongError('');
    setLoading(true);

    fetch('/api/signups', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        type: 'band',
        bandName: data.bandName,
        contactName: data.contactName,
        contactEmail: data.contactEmail,
        song: selectedSong,
        songsKnown: data.songsKnown,
        notes: data.notes,
      }),
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.error || 'Sign-up failed');
        }
        return res.json();
      })
      .then(() => {
        setLoading(false);
        reset();
        setSelectedSong(null);
        toast.success('Band sign-up received!', {
          description: selectedSong
            ? `${data.bandName} is on the list to play "${selectedSong.title}".`
            : `${data.bandName} is on the list!`,
        });
        onSuccess({
          bandName: data.bandName,
          song: selectedSong ?? { id: 'none', title: '', artist: '' },
        });
      })
      .catch((err: Error) => {
        setLoading(false);
        toast.error('Sign-up failed', { description: err.message || 'Please try again.' });
      });
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-5">
      {/* Band name */}
      <div>
        <label htmlFor="bandName" className="label-text">
          Band Name <span className="text-[#FF1493]">*</span>
        </label>
        <p className="helper-text mb-1.5">The name your band will be announced under.</p>
        <input
          id="bandName"
          type="text"
          className={`input-dark ${errors.bandName ? 'border-[#FF1493] focus:border-[#FF1493] focus:ring-[#FF1493]' : ''}`}
          placeholder="e.g. The Rusty Nails"
          {...register('bandName', {
            required: 'Band name is required.',
            minLength: { value: 2, message: 'Must be at least 2 characters.' },
            maxLength: { value: 60, message: 'Keep it under 60 characters.' },
          })}
        />
        {errors.bandName && <p className="error-text">{errors.bandName.message}</p>}
      </div>

      {/* Song selection */}
      <div>
        <label className="label-text">
          Song to Perform <span className="text-[#555] font-normal">(optional)</span>
        </label>
        <p className="helper-text mb-1.5">
          Search for a song from the list, or add your songs in the free text box below.
        </p>
        <SongSearch
          songs={ALL_SONGS}
          selected={selectedSong ? [selectedSong] : []}
          onSelect={(s) => { setSelectedSong(s); setSongError(''); }}
          onRemove={() => setSelectedSong(null)}
          multi={false}
          placeholder="Search for a song or artist…"
          error={songError}
        />
      </div>

      {/* Songs the band knows — free text */}
      <div>
        <label htmlFor="songsKnown" className="label-text">
          Songs Your Band Knows
        </label>
        <p className="helper-text mb-1.5">List the songs your band can play — one per line or comma-separated. Please include the artist's name!</p>
        <textarea
          id="songsKnown"
          rows={4}
          className="input-dark resize-none"
          placeholder={"e.g. Nirvana - Smells Like Teen Spirit\nThe Sex Pistols - Anarchy in the UK\nDead Kennedys - Holiday In Cambodia"}
          {...register('songsKnown')}
        />
      </div>

      {/* Contact name */}
      <div>
        <label htmlFor="contactName" className="label-text">
          Contact Name <span className="text-[#FF1493]">*</span>
        </label>
        <p className="helper-text mb-1.5">Who should we reach out to if we need to get in touch?</p>
        <input
          id="contactName"
          type="text"
          className={`input-dark ${errors.contactName ? 'border-[#FF1493]' : ''}`}
          placeholder="Your full name"
          {...register('contactName', { required: 'Contact name is required.' })}
        />
        {errors.contactName && <p className="error-text">{errors.contactName.message}</p>}
      </div>

      {/* Contact email */}
      <div>
        <label htmlFor="contactEmail" className="label-text">
          Contact Email <span className="text-[#FF1493]">*</span>
        </label>
        <p className="helper-text mb-1.5">We&apos;ll only use this to confirm your slot.</p>
        <input
          id="contactEmail"
          type="email"
          className={`input-dark ${errors.contactEmail ? 'border-[#FF1493]' : ''}`}
          placeholder="band@example.co.uk"
          {...register('contactEmail', {
            required: 'Email is required.',
            pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Enter a valid email address.' },
          })}
        />
        {errors.contactEmail && <p className="error-text">{errors.contactEmail.message}</p>}
      </div>

      {/* Optional notes */}
      <div>
        <label htmlFor="notes" className="label-text">
          Notes <span className="text-[#555] font-normal">(optional)</span>
        </label>
        <p className="helper-text mb-1.5">Equipment needs, special requirements, anything we should know.</p>
        <textarea
          id="notes"
          rows={3}
          className="input-dark resize-none"
          placeholder="e.g. Need a DI box for the bass, bringing our own amp…"
          {...register('notes')}
        />
      </div>

      {/* Submit */}
      <button
        type="submit"
        disabled={loading}
        className="btn-primary w-full flex items-center justify-center gap-2 py-3.5 text-base"
        style={{ minHeight: '52px' }}
      >
        {loading ? (
          <>
            <Loader2 size={18} className="animate-spin" />
            <span>Signing up…</span>
          </>
        ) : (
          <>
            <Users size={18} />
            <span>Put Us on the List</span>
          </>
        )}
      </button>
    </form>
  );
}